import { MathJax, MathJaxContext } from 'better-react-mathjax'
import ReactMarkdown from 'react-markdown'
import remarkMath from 'remark-math'
import rehypeMathjax from 'rehype-mathjax'
import rehypeRaw from 'rehype-raw'
import { isEmpty } from 'lodash'
import { useMemo } from 'react'
import { PluggableList } from 'unified'

const katexOptions = {
    strict: false,
}

type MarkdownRendererProps = {
    children: string
    escapeBackslashses?: boolean
    escapeNewlines?: boolean
    allowRawHtml?: boolean
}

/**
 * @param props.children - The markdown content to render.
 * @param props.escapeBackslashses - Whether to escape backslashes outside of math mode.
 * @param props.escapeNewlines - Whether to escape newlines.
 * @param props.allowRawHtml -
 * Whether to allow raw html tags in the markdown content.
 * It should be used with caution as it can be a security risk.
 */
const MarkdownRenderer = (props: MarkdownRendererProps) => {
    const rehypePlugins: PluggableList = useMemo(() => {
        if (props.allowRawHtml) {
            return [
                [rehypeMathjax, katexOptions],
                [
                    rehypeRaw,
                    {
                        passThrough: ['element'],
                    },
                ],
            ]
        }
        return [[rehypeMathjax, katexOptions]]
    }, [props.allowRawHtml])

    const getFormattedString = (input: string) => {
        let formattedString = input
        if (props.escapeBackslashses) {
            formattedString = escapeBackslashesOutsideDollar(input)
        }

        if (props.escapeNewlines) {
            formattedString = formattedString.replace(/\\n/g, '  ')
        }

        return formattedString
    }

    const isMarkdownImageStart = (
        inMathMode: boolean,
        inImageUrl: boolean,
        index: number,
        input: string
    ) => {
        const char = input[index]

        return (
            !inMathMode &&
            !inImageUrl &&
            char === '!' &&
            index + 1 < input.length &&
            input[index + 1] === '['
        )
    }

    const isMarkdownImageEnd = (inImageUrl: boolean, index: number, input: string) => {
        const char = input[index]

        return inImageUrl && char === ')'
    }

    const escapeBackslashesOutsideDollar = (input: string) => {
        let inMathMode = false
        let inImageUrl = false
        let result = ''
        let i = 0

        while (i < input.length) {
            const char = input[i]

            // Check for markdown image syntax start
            if (isMarkdownImageStart(inMathMode, inImageUrl, i, input)) {
                // Find the closing ] and opening (
                const bracketEnd = input.indexOf(']', i + 2)
                if (
                    bracketEnd !== -1 &&
                    bracketEnd + 1 < input.length &&
                    input[bracketEnd + 1] === '('
                ) {
                    // We're at the start of an image, add the ![alt]( part without processing
                    const altEnd = bracketEnd + 2
                    result += input.substring(i, altEnd)
                    i = altEnd
                    inImageUrl = true
                    continue
                }
            }

            // Check for end of image URL
            if (isMarkdownImageEnd(inImageUrl, i, input)) {
                result += char
                i++
                inImageUrl = false
                continue
            }

            if (inImageUrl) {
                result += char
                i++
                continue
            }

            if (char === '$') {
                inMathMode = !inMathMode
            }

            if (char === '\\' && !inMathMode) {
                result += '\\\\'
            } else {
                result += char
            }

            i++
        }

        return result
    }

    if (isEmpty(props.children)) {
        return null
    }

    return (
        <MathJaxContext
            config={{
                tex: {
                    inlineMath: [
                        ['\\(', '\\)'],
                        ['$', '$'],
                    ],
                },
            }}
        >
            <MathJax dynamic hideUntilTypeset="every">
                <ReactMarkdown
                    remarkPlugins={[remarkMath]}
                    rehypePlugins={rehypePlugins}
                    urlTransform={url => {
                        // Preserve data URLs and other URLs as-is
                        return url
                    }}
                >
                    {getFormattedString(props.children)}
                </ReactMarkdown>
            </MathJax>
        </MathJaxContext>
    )
}

export default MarkdownRenderer
